import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { collection, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { WorkspaceProvider } from '../../context/WorkspaceContext';
import RouteSuspenseFallback from '../RouteSuspenseFallback';

const CodeSandboxReview = lazy(() => import('../../pages/CodeSandboxReview'));

/**
 * Code review workspace with optional company picker for platform super admins.
 * @param {{
 *   userData?: { role?: string, companyId?: string, email?: string },
 *   companies?: Array<{ id: string, name?: string }>,
 * }} props
 */
export default function CodeSandboxReviewHub({ userData, companies: companiesProp = [] }) {
  const role = userData?.role;
  const linkedCompanyId = userData?.companyId || '';
  const needsCompanyPicker = role === 'super_admin' && !linkedCompanyId;

  const [selectedCompanyId, setSelectedCompanyId] = useState(linkedCompanyId);

  const companiesQuery = useMemo(() => {
    if (!needsCompanyPicker) return null;
    return query(collection(db, 'companies'));
  }, [needsCompanyPicker]);
  const { data: companiesLive, loading: companiesLoading } = useFirestoreQuery(
    companiesQuery,
    { enabled: needsCompanyPicker },
  );
  const companies = companiesProp.length > 0 ? companiesProp : companiesLive;

  useEffect(() => {
    if (linkedCompanyId) {
      setSelectedCompanyId(linkedCompanyId);
      return;
    }
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [linkedCompanyId, companies, selectedCompanyId]);

  const effectiveUserData = useMemo(
    () => ({
      ...(userData || {}),
      companyId: linkedCompanyId || selectedCompanyId || null,
    }),
    [userData, linkedCompanyId, selectedCompanyId],
  );

  if (needsCompanyPicker && companiesLoading && companies.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (needsCompanyPicker && companies.length === 0) {
    return (
      <Alert severity="info" sx={{ maxWidth: 640 }}>
        No companies exist yet. Create a company from the Super Admin console before reviewing
        AI code patches.
      </Alert>
    );
  }

  const activeCompanyId = effectiveUserData.companyId;

  return (
    <Box>
      {needsCompanyPicker && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
            Platform super admins are not linked to a single tenant. Choose a company to load its
            pending AI code patches and delegated agent work.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 280, mb: 3 }}>
            <InputLabel id="code-review-company-label">Company</InputLabel>
            <Select
              labelId="code-review-company-label"
              label="Company"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name || c.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {activeCompanyId ? (
        <WorkspaceProvider companyId={activeCompanyId}>
          <Suspense fallback={<RouteSuspenseFallback />}>
            <CodeSandboxReview userData={effectiveUserData} />
          </Suspense>
        </WorkspaceProvider>
      ) : (
        <Alert severity="warning">Select a company to review AI code patches.</Alert>
      )}
    </Box>
  );
}
